// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { Deal } from '@interfaces/deal.interfaces';
import { DocumentStatus } from '@interfaces/document.interfaces';
import { UserTypes } from '@interfaces/user.interfaces';
import { DealershipService } from '@services/dealership.service';
import { DealsService } from '@services/deals.service';
import { DocumentsService } from '@services/documents.service';
import { UserService } from '@services/user.service';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap, takeWhile, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DealAndDocResolver implements Resolve<Observable<Deal>>, OnDestroy {
    loadedDeal: Deal;
    isComponentAlive = true;

    constructor(
        private readonly dealsService: DealsService,
        private readonly documentsService: DocumentsService,
        private readonly userService: UserService,
        private readonly router: Router,
        private readonly dealershipService: DealershipService
    ) {
        this.loadedDeal = null;
    }

    async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<Observable<Deal>> {
        this.dealershipService.dealershipUpdate$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(() => {
            if (this.loadedDeal?.dealerId) {
                this.userService.setUserCurrentDealer(this.loadedDeal?.dealerId);
            }
        });

        if (route.params.dealId === undefined) {
            this.loadedDeal = null;
            return null;
        } else {
            return (await this.getDeal$(+route.params.dealId, +route.params.docId, route)).pipe(map(() => this.loadedDeal));
        }
    }

    async getDeal$(dealId: number, docId: number, route: any) {
        await firstValueFrom(this.userService.user$());

        return this.dealsService.getDeal$(dealId).pipe(
            tap(async (deal) => {
                if (!deal && this.userService.user.userType == UserTypes.Customer) {
                    this.router.navigate(['/main', 'vehicles']);
                    return;
                }

                if (!deal && (this.userService.user.userType == UserTypes.Dealer || this.userService.user.userType == UserTypes.Internal)) {
                    if (route['_routerState'].url.includes('/documents')) {
                        this.router.navigate(['/dashboard', 'documents']);
                        return;
                    } else {
                        this.router.navigate(['/dashboard', 'deals']);
                        return;
                    }
                }

                this.loadedDeal = deal;
            }),
            switchMap((deal) => {
                // Could already have the documents from the dealDocsResolver
                if (this.documentsService.documents && this.documentsService.dealId === dealId) {
                    return of(this.documentsService.documents);
                } else {
                    return this.documentsService.getDocsList$(deal.dealId);
                }
            }),
            map((docs) => {
                if (docs.filter((doc) => doc.documentId === docId).length === 0) {
                    if (
                        route['_routerState'].url.includes('/documents') &&
                        (this.userService.user.userType === UserTypes.Dealer || this.userService.user.userType === UserTypes.Internal)
                    ) {
                        this.router.navigate(['/dashboard', 'documents', dealId]);
                        return;
                    }

                    this.userService.user.userType === UserTypes.Customer
                        ? this.router.navigate(['/main', 'vehicles', dealId])
                        : this.router.navigate(['/dashboard', 'deals', dealId]);
                    return;
                }

                this.loadedDeal.dealDocs = {
                    complete: docs.filter((doc) => doc.documentStatusId === DocumentStatus.Complete),
                    buyer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.BuyerQueue),
                    cobuyer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.CoBuyerQueue),
                    dealer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.DealerQueue),
                    signingCertInProcess: docs.filter((doc) => doc.documentStatusId === DocumentStatus.EnqueueCertification),
                    interventionRequired: docs.filter((doc) => doc.documentStatusId === DocumentStatus.InterventionRequired),
                    deleted: docs.filter((doc) => doc.documentStatusId === DocumentStatus.Deleted),
                    notarizeQueue: docs.filter((doc) => doc.documentStatusId === DocumentStatus.NotarizeQueue),
                };
                this.loadedDeal;
            })
        );
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
